import { ContractTrackingTimelineDto } from "@/orval/gen"
import { PersonNameWithUserNameFallback } from "@/routes/-components/chat/ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { Timeline, Text, Menu, ActionIcon, Tooltip } from "@mantine/core"
import { useTranslation } from "react-i18next"
import {
  ContractTrackingIcon,
  ContractTrackingIconVariant,
} from "./ContractTrackingIcon"
import { getDateOnly } from "@components/date-only"
import { ApproveContractModal } from "../ApproveContractModal"
import { useDisclosure } from "@mantine/hooks"
import { SetDeadlineModal } from "./SetDeadlineModal"
import { useBatchedSimplePerson } from "@/-dataloaders/simple-persons"
import { IconMailShare } from "@tabler/icons-react"
import { SendMailModal } from "./SendMailModal"

interface Props {
  contractId: number
  workflows: ContractTrackingTimelineDto[]
  personId: number
}

export function ContractTrackingTimeline({
  contractId,
  workflows,
  personId,
}: Props) {
  const { t } = useTranslation("app_server")

  const { data: person } = useBatchedSimplePerson(personId)

  const [setStatusOpened, { open: openSetStatus, close: closeSetStatus }] =
    useDisclosure()
  const [
    setDeadlineOpened,
    { open: openSetDeadline, close: closeSetDeadline },
  ] = useDisclosure()
  const [sendMailOpened, { open: openSendMail, close: closeSendMail }] =
    useDisclosure()
  const [defaultStatusId, setDefaultStatusId] = React.useState<
    number | undefined
  >()
  const handleClickSetStatus = (statusId: number) => {
    setDefaultStatusId(statusId)
    openSetStatus()
  }
  const handleClickSetDeadline = (statusId: number) => {
    setDefaultStatusId(statusId)
    openSetDeadline()
  }

  const activeWorkflow = workflows.findIndex((w) => w.active)

  const items = workflows.map((step, i) => {
    let color = undefined
    let variant: ContractTrackingIconVariant = "default"
    if (step.tracking) {
      if (step.tracking.isDeadline) {
        if (step.tracking.trackingDate <= getDateOnly()) {
          color = "red"
        }
        variant = "deadline"
      } else {
        variant = "done"
      }
    }
    if (step.isAvailable) {
      if (variant === "deadline") {
        variant = "deadline-todo"
      } else if (variant === "done") {
        variant = "done-todo"
      } else {
        variant = "todo"
      }
    }
    const menuDisabled = step.isAvailable ? false : i <= activeWorkflow
    return (
      <Timeline.Item
        color="blue.5"
        bullet={
          step.signatureType === "External" && !step.tracking ? (
            <Tooltip
              disabled={step.isAvailable}
              label={t("ContractTracking_SendMailTooltip")}
            >
              <ActionIcon
                disabled={!step.isAvailable}
                onClick={openSendMail}
                radius={"lg"}
                px={4}
                variant="filled"
                color="blue"
              >
                <IconMailShare />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Menu disabled={menuDisabled}>
              <Menu.Target>
                <ContractTrackingIcon
                  variant={variant}
                  style={{
                    cursor: !menuDisabled ? "pointer" : undefined,
                  }}
                  {...(color ? { color } : {})}
                />
              </Menu.Target>
              <Menu.Dropdown>
                {step.isAvailable && (
                  <Menu.Item
                    onClick={() =>
                      handleClickSetStatus(
                        step.contractApprovalStatus.contractApprovalStatusId
                      )
                    }
                  >
                    {t("ContractTracking_SetStatus")}
                  </Menu.Item>
                )}
                <Menu.Item
                  color="red"
                  onClick={() =>
                    handleClickSetDeadline(
                      step.contractApprovalStatus.contractApprovalStatusId
                    )
                  }
                >
                  {t("ContractTracking_SetDeadline")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )
        }
        key={i}
        title={step.contractApprovalStatus.name}
      >
        {!!step.tracking &&
          (i <= activeWorkflow || step.tracking.isDeadline) && (
            <>
              <Text>
                {step.tracking.isDeadline ? (
                  <>
                    {t("DeadlineUntil", {
                      date: getFormattedDate(step.tracking?.trackingDate),
                    })}
                  </>
                ) : (
                  getFormattedDate(step.tracking?.trackingDate)
                )}{" "}
                {step.signatureType === "External" &&
                !step.tracking.isDeadline ? (
                  t("ContractApproval_ExternalPersonName", {
                    personName: person?.name,
                  })
                ) : step.tracking.isDeadline ? (
                  ""
                ) : (
                  <PersonNameWithUserNameFallback
                    userId={step.tracking.userId}
                  />
                )}
              </Text>
              <Text c={"dimmed"} truncate style={{ whiteSpace: "pre-wrap" }}>
                {step.tracking.note}
              </Text>
            </>
          )}
      </Timeline.Item>
    )
  })

  return (
    <>
      <Timeline m={"md"} active={activeWorkflow + 1}>
        {items}
      </Timeline>
      <ApproveContractModal
        contractId={contractId}
        defaultStatusId={defaultStatusId}
        opened={setStatusOpened}
        onClose={closeSetStatus}
      />
      <SetDeadlineModal
        contractId={contractId}
        statusId={defaultStatusId}
        opened={setDeadlineOpened}
        onClose={closeSetDeadline}
      />
      <SendMailModal
        contractId={contractId}
        opened={sendMailOpened}
        onClose={closeSendMail}
      />
    </>
  )
}
